import { FC, useMemo } from 'react';
import { MetaTitle } from 'src/components/seo/MetaTitle';
import { publicUrl } from 'vendor/utils/publicUrl';
import { Language, Person, VideoObject } from 'schema-dts';
import { LDJSON } from 'src/components/seo/LDJSON';
import { route } from 'src/services/route';
import { MetaCanonical } from 'src/components/seo/MetaCanonical';
import { MetaImage } from 'src/components/seo/MetaImage';
import { MetaDescription } from 'src/components/seo/MetaDescription';
import { Videos } from 'src/graphql/schema.graphql';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { useTranslate } from 'react-t7e';
import { getDirectusImageAssetUrl } from 'src/services/directus/common';

export const VideoPageSEO: FC<{
  video: Videos;
}> = ({ video: untranslatedVideo }) => {
  const translateItem = useTranslateItem();
  const { _ } = useTranslate();

  const video = translateItem(untranslatedVideo);

  const canonicalUrl = publicUrl(route.video(video).page(true));
  const thumbnailUrl = publicUrl(
    getDirectusImageAssetUrl(video.thumbnail.id, {
      width: 1200,
      height: 630,
      fit: 'cover',
      format: 'jpg',
    }),
  );

  const meta: VideoObject = useMemo(
    () => ({
      '@type': 'VideoObject',
      name: video.name,
      description: video.description,
      thumbnailUrl,
      uploadDate: video.date_created,
      url: canonicalUrl,
      abstract: video.excerpt || undefined,
      inLanguage: video.audioLanguages
        ? video.audioLanguages
            .map((t) => t.languages_code)
            .map(
              (lang): Language => ({
                '@type': 'Language',
                name: lang.name,
              }),
            )
        : [],
      actor: video.speakers
        ? video.speakers
            .map((t) => t.people_id)
            .map(translateItem)
            .map(
              (speaker): Person => ({
                '@type': 'Person',
                jobTitle: [speaker.degree, speaker.title].filter((v) => !!v),
                ...(speaker.avatar?.id
                  ? {
                      image: publicUrl(
                        getDirectusImageAssetUrl(speaker.avatar.id),
                      ),
                    }
                  : {}),
              }),
            )
        : [],
    }),
    [video, canonicalUrl, thumbnailUrl, translateItem],
  );

  return (
    <>
      <MetaTitle>
        {_('Video - {videoName}', undefined, {
          videoName: video.name,
        })}
      </MetaTitle>
      <MetaDescription>
        {_('Watch online medical aesthetic videos. {videoExcerpt}', undefined, {
          videoExcerpt: video.excerpt ?? '',
        })}
      </MetaDescription>
      <MetaImage src={thumbnailUrl} />
      <MetaCanonical href={canonicalUrl} />
      <LDJSON data={meta} />
    </>
  );
};
